// Shared helpers for ContentIQ uploaded assets — used by the ContentIQ
// workspace (Stage 1 sources) and the Content Assets page.
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

// Auth rides the axios interceptor, so downloads go blob -> object URL
// (a plain <a href> would arrive without the JWT).
export async function downloadAssetBlob(projectId, asset) {
  const res = await axios.get(`${API}/contentiq/projects/${projectId}/assets/${asset.id}/download`,
    { responseType: 'blob' });
  saveBlob(res.data, asset.title || asset.original_filename || 'download');
}

// Same blob->object-URL pattern for generated deliverables (.docx endpoints
// under /contentiq) — any authenticated GET that streams a file.
export async function downloadFileBlob(url, filename) {
  const res = await axios.get(url, { responseType: 'blob' });
  saveBlob(res.data, filename);
}

// Hand the browser a Blob as a named download — also used for files built in
// the browser (the sales enablement deck, utils/deck/renderDeck.js).
export function saveBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export const MIME_LABELS = {
  'application/pdf': 'pdf', 'text/plain': 'txt', 'text/markdown': 'md', 'text/csv': 'csv',
  'image/png': 'png', 'image/jpeg': 'jpg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
};

export const fmtSize = (b) => (b == null ? '—'
  : b >= 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB`
  : `${Math.max(1, Math.round(b / 1024))} KB`);

// Upload size limits — mirror the backend's app/contentiq/services/assets_s3.py
// (MAX_FILE_BYTES for an asset, MAX_BRAND_FILE_BYTES for a client logo). The
// wizard checks them BEFORE sending: a file past nginx's body limit never
// reaches the server's readable reason — nginx answers with an HTML 413 page,
// which read "✕ HTTP 413" after minutes of uploading (Edward's staging test
// 2026-10-29: a 52.7 MB client ebook against the 25 MB cap).
export const CIQ_ASSET_MAX_BYTES = 25 * 1024 * 1024;
export const CIQ_BRAND_MAX_BYTES = 10 * 1024 * 1024;

const limitText = (maxBytes) => `${Math.round(maxBytes / (1024 * 1024))} MB`;

// Why a file is too big to send, or null when it fits. Short — it sits in an
// upload progress row.
export function oversizeReason(file, maxBytes) {
  if (!file || !(file.size > maxBytes)) return null;
  return `too large: ${fmtSize(file.size)} (${limitText(maxBytes)} max)`;
}

// What to do about it, appended once to a batch's error line.
export const oversizeAdvice = (maxBytes) =>
  `Files over ${limitText(maxBytes)} can't be added — compress the PDF `
  + '(e.g. Acrobat’s Reduce File Size) and drop it again.';

// A failed upload's reason, from the error axios threw: the server's own
// message when it sent one, plain words for nginx's 413, else the status.
export function uploadErrorText(e, maxBytes) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  const status = e?.response?.status;
  if (status === 413) return `too large for the server (${limitText(maxBytes)} max)`;
  return status ? `HTTP ${status}` : 'network error';
}
